import React from 'react'
import { MediaObject } from 'types'
import { VideoDuration } from 'components/VideoDuration/VideoDuration'
import { MediaFileViewerModal } from 'components/MediaFileViewerModal/MediaFileViewerModal'
import { useModals } from 'almanac-ui/Modal'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { isVideo } from 'components/ImagePickerModal/utils'
import { VideoPlayer } from '../VideoPlayer/VideoPlayer'
import Styles from './MediaRenderer.module.scss'
import { ConditionalWrapper } from 'common/ConditionalWrapper/ConditionalWrapper'
import { EditInDesignerOverlay } from '../../components/EditInDesignerOverlay/EditInDesignerOverlay'
import cx from 'classnames'

interface MediaRendererProps {
  mediaObject: MediaObject
  alt: string
  className?: string
  showVideoControls?: boolean
  isActive?: boolean
  onClick?: () => void
  style?: React.CSSProperties
  isPressable?: boolean
  isGenerating?: boolean
  showHoverActionOverlay?: boolean
  almanacFileKey?: string
  onClose?: () => void
  hideAudioTransformButtons?: boolean
}

export const MediaRenderer: React.FC<MediaRendererProps> = ({
  mediaObject,
  alt,
  className,
  showVideoControls = false,
  showHoverActionOverlay = false,
  almanacFileKey,
  isActive = true,
  isGenerating = false,
  onClick,
  style,
  isPressable = true,
  onClose,
  hideAudioTransformButtons = false,
}) => {
  const { openModal } = useModals()
  const { isDesktop } = useResponsiveDesign()

  const isVideoContent = isVideo({ url: mediaObject.source_url, type: mediaObject.mime_type })
  const imageSrc = mediaObject.thumbnail_url || mediaObject.source_url

  const handleClick = () => {
    if (onClick) {
      onClick()
    } else if (isDesktop) {
      if (!isVideoContent || !showVideoControls) {
        openModal(MediaFileViewerModal, { file: mediaObject, editEnabled: false, showTitle: false })
      }
    }
  }

  return (
    <ConditionalWrapper
      condition={showHoverActionOverlay && almanacFileKey}
      wrapper={(children) => (
        <EditInDesignerOverlay
          almanacFileKey={almanacFileKey as string}
          borderRadius={0}
          position={isVideoContent ? 'top' : undefined}
          onClose={onClose}
          scrimOpacity={40}
        >
          {children}
        </EditInDesignerOverlay>
      )}
    >
      {isVideoContent && showVideoControls ? (
        <VideoPlayer
          mediaObject={mediaObject}
          isActive={isActive}
          className={className}
          almanacFileKey={almanacFileKey as string}
          isGenerating={isGenerating}
          hideAudioTransformButtons={hideAudioTransformButtons}
        />
      ) : (
        <img
          src={imageSrc}
          alt={alt}
          className={cx(className, Styles.image, { [Styles.nonPressable]: !isPressable })}
          style={style}
          onClick={isPressable ? handleClick : undefined}
        />
      )}
      {isVideoContent && mediaObject.duration && <VideoDuration duration={mediaObject.duration} />}
    </ConditionalWrapper>
  )
}
